import React from "react";
import { Copy, Trash2, Pencil, X, Check, Power, QrCode, History, Bell, ClipboardList, MapPinned, UserRound } from "lucide-react";
import { formatDate, formatDateShort, isExpired } from "./codes-utils";

const TOGGLE_ACTIVE = {
  borderColor: "rgba(134, 239, 172, 0.3)",
  background: "rgba(22, 101, 52, 0.1)",
  color: "#86EFAC",
};
const TOGGLE_DISABLED = {
  borderColor: "rgba(252, 165, 165, 0.3)",
  background: "rgba(153, 27, 27, 0.1)",
  color: "#FCA5A5",
};

export default function CodeRow({
  code: c,
  files,
  editing,
  editValues,
  onEditValueChange,
  onCopy,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggleActive,
  onDelete,
  onQR,
  onLogs,
  onTrace,
  onOwner,
  onSheet,
}) {
  const isEditing = editing;
  return (
    <tr className="row border-t" style={{ borderColor: "var(--vault-border)" }} data-testid={`code-row-${c.code}`}>
      <td className="px-4 py-4">
        <div className="inline-flex items-center gap-2">
          <button
            onClick={() => onCopy(c.code)}
            className="code-chip inline-flex items-center gap-2 hover:bg-[rgba(197,160,89,0.15)]"
            data-testid={`copy-code-${c.code}`}
          >
            {c.code}
            <Copy className="w-3 h-3" />
          </button>
          {c.notify_email && (
            <span title={`Notifies ${c.notify_email}`} className="inline-flex items-center" data-testid={`notify-indicator-${c.code}`}>
              <Bell className="w-3.5 h-3.5" style={{ color: "var(--vault-gold)" }} />
            </span>
          )}
        </div>
      </td>

      <td className="px-4 py-4 text-[var(--vault-text-muted)]">
        {isEditing ? (
          <input
            value={editValues.label}
            onChange={(e) => onEditValueChange("label", e.target.value)}
            className="px-2 py-1 rounded text-sm w-40 outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          />
        ) : (c.label || "—")}
      </td>

      <td className="px-4 py-4 text-[var(--vault-text-muted)]">
        {isEditing ? (
          <select
            value={editValues.fileId}
            onChange={(e) => onEditValueChange("fileId", e.target.value)}
            className="px-2 py-1 rounded text-sm outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
          >
            <option value="">— None —</option>
            {files.map((f) => (
              <option key={f.id} value={f.id}>{f.original_filename}</option>
            ))}
          </select>
        ) : c.file_name ? (
          <span className="text-white font-mono-vault text-xs">{c.file_name}</span>
        ) : (
          <span className="text-yellow-400/70">Unassigned</span>
        )}
      </td>

      <td className="px-4 py-4 font-mono-vault">
        {isEditing ? (
          <input
            type="number"
            min="0"
            value={editValues.maxUses}
            onChange={(e) => onEditValueChange("maxUses", e.target.value)}
            className="px-2 py-1 rounded text-sm w-20 outline-none"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}
            placeholder="∞"
          />
        ) : (
          <span>
            {c.access_count}
            {c.max_uses ? <span className="text-[var(--vault-text-faint)]"> / {c.max_uses}</span> : <span className="text-[var(--vault-text-faint)]"> / ∞</span>}
          </span>
        )}
      </td>

      <td className="px-4 py-4 text-xs">
        {isEditing ? (
          <input
            type="datetime-local"
            value={editValues.expiresAt}
            onChange={(e) => onEditValueChange("expiresAt", e.target.value)}
            className="px-2 py-1 rounded outline-none text-xs"
            style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)", colorScheme: "dark" }}
          />
        ) : c.expires_at ? (
          <span style={{ color: isExpired(c.expires_at) ? "#FCA5A5" : "var(--vault-text-muted)" }}>
            {formatDateShort(c.expires_at)}
            {isExpired(c.expires_at) && <span className="block text-[10px]">expired</span>}
          </span>
        ) : (
          <span className="text-[var(--vault-text-faint)]">Never</span>
        )}
      </td>

      <td className="px-4 py-4 text-[var(--vault-text-muted)] text-xs">{formatDate(c.last_accessed_at)}</td>

      <td className="px-4 py-4">
        <button
          onClick={() => onToggleActive(c)}
          data-testid={`toggle-${c.code}`}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors"
          style={c.is_active ? TOGGLE_ACTIVE : TOGGLE_DISABLED}
        >
          <Power className="w-3 h-3" />
          {c.is_active ? "Active" : "Disabled"}
        </button>
      </td>

      <td className="px-4 py-4 text-right sticky right-0 z-10" style={{ background: "var(--vault-surface)" }}>
        <div className="inline-flex items-center gap-1 whitespace-nowrap">
          {isEditing ? (
            <>
              <button
                type="button"
                onClick={() => onSaveEdit(c.id)}
                data-testid={`save-${c.code}`}
                className="btn-gold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium"
                title="Save changes"
              >
                <Check className="w-3.5 h-3.5" />
                Save
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                data-testid={`cancel-${c.code}`}
                className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs"
                title="Discard changes"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={() => onSheet(c)} className="p-2 rounded hover:bg-white/5 relative" data-testid={`sheet-${c.code}`} title={c.product_sheet ? "Edit product sheet" : "Add product sheet"}>
                <ClipboardList className="w-4 h-4" style={{ color: c.product_sheet ? "var(--vault-gold)" : "var(--vault-text-muted)" }} />
                {c.photo_count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] font-mono-vault font-bold rounded-full px-1 min-w-[14px] h-[14px] flex items-center justify-center" style={{ background: "var(--vault-gold)", color: "var(--vault-bg)" }}>
                    {c.photo_count}
                  </span>
                )}
              </button>
              <button onClick={() => onQR(c)} className="p-2 rounded hover:bg-white/5" data-testid={`qr-${c.code}`} title="QR code">
                <QrCode className="w-4 h-4 text-[var(--vault-text-muted)]" />
              </button>
              <button onClick={() => onLogs(c)} className="p-2 rounded hover:bg-white/5" data-testid={`logs-${c.code}`} title="Access log">
                <History className="w-4 h-4 text-[var(--vault-text-muted)]" />
              </button>
              <button onClick={() => onOwner(c)} className="p-2 rounded hover:bg-white/5" data-testid={`owner-${c.code}`} title={c.owner_email || c.owner_phone ? "Bottle owner contact" : "Set bottle owner contact"}>
                <UserRound className="w-4 h-4" style={{ color: c.owner_email || c.owner_phone ? "var(--vault-gold)" : "var(--vault-text-muted)" }} />
              </button>
              <button onClick={() => onTrace(c)} className="p-2 rounded hover:bg-white/5" data-testid={`trace-${c.code}`} title="Traceability map">
                <MapPinned className="w-4 h-4 text-[var(--vault-text-muted)]" />
              </button>
              <button onClick={() => onStartEdit(c)} className="p-2 rounded hover:bg-white/5" data-testid={`edit-${c.code}`} title="Edit">
                <Pencil className="w-4 h-4 text-[var(--vault-text-muted)]" />
              </button>
              <button onClick={() => onDelete(c.id)} className="p-2 rounded hover:bg-white/5" data-testid={`delete-${c.code}`} title="Delete">
                <Trash2 className="w-4 h-4 text-red-400/80" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
